import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Menu, X, ChevronDown } from 'lucide-react';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from '@/components/ui/navigation-menu';
import { cn } from '@/lib/utils';
import { PADMORA_ENQUIRY_CTA_LABEL, PADMORA_ENQUIRY_MAILTO } from '@/lib/constants';

const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 8);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { label: 'Brands', href: '/brands' },
    { label: 'Platform', href: '/platform' },
    { label: 'Compliance', href: '/compliance' },
    { label: 'Store', href: '/store' },
    { label: 'About', href: '/about' }
  ];

  // Trading submenu items
  const tradingItems = [
    { label: 'Trading Overview', href: '/trading' },
    { label: 'Ammonium Sulphate → USA', href: '/trading/amsul-usa', description: 'Forward CFR deals for US seasonal peaks' },
    { label: 'Palm Products → World', href: '/trading/palm-products', description: 'LC-backed palm oil & olein to World with deep supplier network' },
    { label: 'Base Oil → Africa', href: '/trading/base-oil', description: 'LC-backed base oil (Group I/II) to Kenya, Tanzania & Uganda' },
    { label: 'Consumer Pack → Caribbean', href: '/consumer-pack', description: 'Direct distribution of consumer-packaged products from ASEAN to Caribbean' }
  ];

  const isActive = (href: string) => {
    return location.pathname === href;
  };

  const isTradingActive = () => {
    return location.pathname.startsWith('/trading');
  };

  return (
    <motion.nav
      className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-lg border-b border-border"
      animate={{
        boxShadow: isScrolled ? '0 4px 20px rgba(0, 0, 0, 0.1)' : '0 0 0 rgba(0, 0, 0, 0)',
      }}
      transition={{ duration: 0.2 }}
    >
      <div className="container mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
            <div className="w-12 h-12 rounded-full flex items-center justify-center border-2 border-[#1451af] overflow-hidden">
              <img
                src="/logo.png"
                alt="Padmora Logo"
                className="w-16 h-16 object-cover rounded-full"
              />
            </div>
            <div className="flex flex-col">
              <div className="text-2xl font-800 text-[#1451af] tracking-tight">
                Padmora
              </div>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6">
            {/* Trading Dropdown */}
            <NavigationMenu>
              <NavigationMenuList>
                <NavigationMenuItem>
                  <NavigationMenuTrigger 
                    className={cn(
                      "text-sm font-medium",
                      "!bg-transparent hover:!bg-transparent focus:!bg-transparent data-[state=open]:!bg-transparent",
                      "!transition-colors",
                      isTradingActive() 
                        ? '!text-primary hover:!text-primary focus:!text-primary data-[state=open]:!text-primary' 
                        : '!text-muted-foreground hover:!text-primary focus:!text-primary data-[state=open]:!text-primary'
                    )}
                  >
                    Trading
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <ul className="grid w-[400px] gap-3 p-4">
                      {tradingItems.map((item) => (
                        <li key={item.href}>
                          <NavigationMenuLink asChild>
                            <Link
                              to={item.href}
                              className={cn(
                                "block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground",
                                isActive(item.href) && "bg-accent/50"
                              )}
                            >
                              <div className="text-sm font-medium leading-none">{item.label}</div>
                              {item.description && (
                                <p className="line-clamp-2 text-xs leading-snug text-muted-foreground">
                                  {item.description}
                                </p>
                              )}
                            </Link>
                          </NavigationMenuLink>
                        </li>
                      ))}
                    </ul>
                  </NavigationMenuContent>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>

            {/* Other Nav Items */}
            {navItems.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                className={`text-sm font-medium transition-colors hover:text-primary ${
                  isActive(item.href) ? 'text-primary' : 'text-muted-foreground'
                }`}
              >
                {item.label}
              </Link>
            ))}
          </div>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center gap-4">
            <Button
              variant="secondary"
              size="sm"
              asChild
            >
              <a
                href={PADMORA_ENQUIRY_MAILTO}
              >
                {PADMORA_ENQUIRY_CTA_LABEL}
              </a>
            </Button>
          </div>

          {/* Mobile Menu */}
          <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="sm" className="md:hidden">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-80">
              <div className="flex flex-col gap-6 mt-8">
                {/* Trading Section with Submenu */}
                <div className="flex flex-col gap-3">
                  <div className={`text-lg font-medium ${isTradingActive() ? 'text-primary' : 'text-muted-foreground'}`}>
                    Trading
                  </div>
                  <div className="flex flex-col gap-2 ml-4 border-l-2 border-border pl-4">
                    {tradingItems.map((item) => (
                      <Link
                        key={item.href}
                        to={item.href}
                        onClick={() => setIsMobileMenuOpen(false)}
                        className={`text-sm font-medium transition-colors hover:text-primary ${
                          isActive(item.href) ? 'text-primary' : 'text-muted-foreground'
                        }`}
                      >
                        {item.label}
                      </Link>
                    ))}
                  </div>
                </div>

                {/* Other Nav Items */}
                {navItems.map((item) => (
                  <Link
                    key={item.href}
                    to={item.href}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`text-lg font-medium transition-colors hover:text-primary ${
                      isActive(item.href) ? 'text-primary' : 'text-muted-foreground'
                    }`}
                  >
                    {item.label}
                  </Link>
                ))}
                <div className="pt-4 border-t">
                  <Button
                    variant="secondary"
                    size="sm"
                    className="w-full"
                    asChild
                  >
                    <a
                      href={PADMORA_ENQUIRY_MAILTO}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      {PADMORA_ENQUIRY_CTA_LABEL}
                    </a>
                  </Button>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </motion.nav>
  );
};

export default Navigation;